import { Link } from 'react-router'
import { Card, Skeleton } from '@/components/ui'
import { CoverImage } from '@/components/common/CoverImage'
import { formatCount } from '@/lib/format'
import { plural, t } from '@/lib/i18n'
import { humanizeSlug, useCommunity } from '@/lib/community'
import type { CommunitySlotProps } from './slots'

/**
 * Placeholder community card for the right column. S2 provides the real one at
 * src/features/communities/components/CommunitySidebar.tsx (same props) and the shell uses it.
 */
export function CommunitySidebar({ slug, community }: CommunitySlotProps) {
  const { isError } = useCommunity(slug)
  if (!community && isError) {
    return (
      <Card padding="none" className="overflow-hidden">
        <CoverImage name={humanizeSlug(slug)} aspect="1.9/1" rounded={false} />
        <div className="p-4">
          <h2 className="text-section text-text">{humanizeSlug(slug)}</h2>
          <p className="text-meta text-muted">{t('Community details are not available right now.')}</p>
        </div>
      </Card>
    )
  }
  if (!community) {
    return (
      <Card padding="none" className="overflow-hidden" aria-busy="true">
        <Skeleton className="aspect-[1.9/1] w-full rounded-none" />
        <div className="space-y-2 p-4">
          <Skeleton height={18} className="w-2/3" />
          <Skeleton height={12} className="w-1/3" />
          <Skeleton height={12} className="w-full" />
        </div>
      </Card>
    )
  }
  return (
    <Card padding="none" className="overflow-hidden">
      <CoverImage src={community.coverUrl} name={community.name} aspect="1.9/1" rounded={false} />
      <div className="space-y-3 p-4">
        <div>
          <h2 className="text-section text-text">{community.name}</h2>
          <Link to={`/${community.slug ?? slug}/about`} className="text-meta text-muted hover:text-text">
            {window.location.host}/{community.slug ?? slug}
          </Link>
        </div>
        {community.description ? <p className="text-body text-text">{community.description}</p> : null}
        <dl className="grid grid-cols-3 border-t border-line pt-3 text-center">
          <div>
            <dt className="sr-only">{t('Members')}</dt>
            <dd className="text-section tabular">{formatCount(community.memberCount)}</dd>
            <dd className="text-meta text-muted">{plural(community.memberCount, 'Member', 'Members')}</dd>
          </div>
          <div className="border-x border-line">
            <dt className="sr-only">{t('Online')}</dt>
            <dd className="text-section tabular">{formatCount(community.onlineCount ?? 0)}</dd>
            <dd className="text-meta text-muted">{t('Online')}</dd>
          </div>
          <div>
            <dt className="sr-only">{t('Admins')}</dt>
            <dd className="text-section tabular">{formatCount(community.adminCount ?? 1)}</dd>
            <dd className="text-meta text-muted">{plural(community.adminCount ?? 1, 'Admin', 'Admins')}</dd>
          </div>
        </dl>
      </div>
    </Card>
  )
}
